<template>
  <dd-modal
    v-model:visible="visibleProxy"
    title="New Checklist"
    append-to-body
    class="new-checklist-modal"
  >
    <!-- Ctrl/Cmd+Enter submits from either field -->
    <el-form
      label-position="top"
      @submit.prevent
      @keydown.ctrl.enter.prevent="handleSubmit"
      @keydown.meta.enter.prevent="handleSubmit"
    >
      <el-form-item label="Name">
        <el-input
          ref="nameInput"
          v-model="name"
          :maxlength="maxTitleLength"
          label="Name"
          placeholder="e.g. Release checklist"
          @keydown.enter.exact="handleNameEnter"
        />
      </el-form-item>
      <el-form-item :error="itemsError" label="Items">
        <el-input
          ref="itemsInput"
          v-model="itemsText"
          :autosize="{ minRows: 6, maxRows: 12 }"
          :placeholder="itemsPlaceholder"
          label="Items"
          type="textarea"
          resize="none"
        />
        <p v-if="!itemsError" class="new-checklist-modal__hint">{{ itemsHint }}</p>
      </el-form-item>
    </el-form>
    <template #footer>
      <span>
        <dd-button type="text" @click="visibleProxy = false">Cancel</dd-button>
        <dd-button
          :is-disabled="!canSubmit"
          :is-loading="isSubmitting"
          :color="ColorAssistant.getEntityColor(entityType)"
          @click="handleSubmit"
        >Add Checklist</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'

// Checklists.Title is nvarchar(100) and ChecklistItems.Name is nvarchar(150)
const MAX_TITLE_LENGTH = 100
const MAX_ITEM_NAME_LENGTH = 150

export default defineComponent({
  name: 'NewChecklistModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: Number,
      required: true,
    },
    itemId: {
      type: Number,
      required: true,
    },
    isLinkedToTask: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['checklist-created', 'update:visible'],

  data() {
    return {
      ColorAssistant,
      maxTitleLength: MAX_TITLE_LENGTH,
      itemsPlaceholder: 'Write release notes\nUpdate docs\nTell customers',
      name: '',
      itemsText: '',
      isSubmitting: false,
    }
  },

  computed: {
    // Proxy so dd-modal's own close paths (X, Esc) flow back to the parent's .sync
    visibleProxy: {
      get(): boolean {
        return this.visible
      },
      set(newValue: boolean): void {
        this.$emit('update:visible', newValue)
      },
    },
    entityType(): EEntities {
      return this.isLinkedToTask ? EEntities.PROJECTS : EEntities.MAILBOXES
    },
    trimmedName(): string {
      return this.name.trim()
    },
    // Each non-blank line becomes an item
    itemNames(): string[] {
      return this.itemsText
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0)
    },
    itemsError(): string {
      const tooLong = this.itemNames.filter(
        itemName => itemName.length > MAX_ITEM_NAME_LENGTH,
      )
      if (tooLong.length === 0) {
        return ''
      }

      const preview = tooLong[0].substring(0, 30)
      const others = tooLong.length > 1 ? ` and ${tooLong.length - 1} more` : ''
      return `Items can be up to ${MAX_ITEM_NAME_LENGTH} characters. Shorten “${preview}…”${others}.`
    },
    itemsHint(): string {
      const count = this.itemNames.length
      if (count === 0) {
        return 'One item per line'
      }
      return `One item per line · ${count} item${count === 1 ? '' : 's'}`
    },
    canSubmit(): boolean {
      return this.trimmedName.length > 0 && !this.itemsError
    },
  },

  watch: {
    visible(isVisible: boolean) {
      if (isVisible) {
        this.name = ''
        this.itemsText = ''
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.nameInput.focus()
        })
      }
    },
  },

  methods: {
    handleNameEnter(event: Event | KeyboardEvent) {
      // Leave Enter alone while it confirms IME composition (keyCode 229)
      if ((event as KeyboardEvent).keyCode === 229) {
        return
      }
      event.preventDefault()
      // @ts-ignore
      this.$refs.itemsInput.focus()
    },

    async handleSubmit() {
      if (!this.canSubmit || this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      try {
        const checklist = await TasksAPI.post_ChecklistCreate(
          this.projectId,
          this.itemId,
          {
            title: this.trimmedName,
            listChecklistItemNames: this.itemNames,
          },
        )
        // Close before announcing the checklist. The empty-state picker that hosts this modal
        // is removed once a checklist exists, and dd-modal only re-enables body scroll when
        // it sees visible turn false.
        this.visibleProxy = false
        await this.$nextTick()
        this.$emit('checklist-created', checklist)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.new-checklist-modal {
  &__hint {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: var(--color-text-third);
  }
}
</style>
